import React, { useState, useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import Link from "next/link";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import IMAGES from "@/public/assets/img";
import gstSearch from "@/config/gstSearch_config";
import HsnSearchCTA from "@/component/gstNumberData/hsnSearch-CTA";
import { getBreadcrumbSchema } from "@/lib/htsChapterBreadcrumbSchema";
import { getServiceSchema } from "@/lib/serviceSchema";
import { getGstSlug, extractGstinFromSlug } from "@/lib/gstUtils";

const GSTNumberDetail = ({ gstData, slug }) => {
  const router = useRouter();
  const isFound = Boolean(gstData && gstData.found !== false);
  const gstin = gstData?.gstin || extractGstinFromSlug(slug) || "";
  const legalName = gstData?.legalName || "";
  const status = gstData?.status || "Active";
  const constitution = gstData?.constitution || "";
  const entityType = gstData?.entityType || "Regular";
  const registrationDate = gstData?.registrationDate || "";
  const stateJurisdiction = gstData?.stateJurisdiction || "";
  const centreJurisdiction = gstData?.centreJurisdiction || "";
  const principalPlaceOfBusiness = gstData?.principalPlaceOfBusiness || "";
  const pincode = gstData?.pincode || "";
  const pan = gstData?.pan || (gstin.length === 15 ? gstin.slice(2, 12) : "");
  const entityNo = gstin.length === 15 ? gstin.slice(12, 13) : "1";
  const reservedChar = gstin.length === 15 ? gstin.slice(13, 14) : "Z";
  const checkDigit = gstin.length === 15 ? gstin.slice(14, 15) : "";
  const stateCode = gstData?.stateCode || (gstin.length >= 2 ? gstin.slice(0, 2) : "");
  const stateItem = gstSearch.stateCodeSection?.list?.find((item) => item.code === stateCode);
  const state = gstData?.state || stateItem?.name || "";
  const hsnSacCodes = gstData?.hsnSacCodes || [];

  const [gstinInput, setGstinInput] = useState(gstin);
  const [recentSearches, setRecentSearches] = useState([]);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isRequested, setIsRequested] = useState(false);

  const [isSearching, setIsSearching] = useState(false);

  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  const isBtnDisabled =
    !gstinInput ||
    gstinInput.trim() === "" ||
    gstinInput.length < 15 ||
    !gstinRegex.test(gstinInput) ||
    isSearching;

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("recentGstSearches");
        let list = saved ? JSON.parse(saved) : [];
        if (!Array.isArray(list)) list = [];
        if (gstin && gstinRegex.test(gstin)) {
          list = [gstin, ...list.filter((item) => item !== gstin)].slice(0, 3);
          localStorage.setItem("recentGstSearches", JSON.stringify(list));
        }
        setRecentSearches(list);
      } catch (e) {
        console.error("Error managing recent GST searches:", e);
      }
    }
  }, [gstin]);

  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (val.length <= 15) {
      setGstinInput(val);
    }
  };

  const handleSearch = async (gstinToSearch) => {
    const target = (gstinToSearch || gstinInput || "").trim().toUpperCase();
    if (!target || target.length !== 15 || !gstinRegex.test(target)) return;

    const updated = [target, ...recentSearches.filter((item) => item !== target)].slice(0, 3);
    setRecentSearches(updated);
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("recentGstSearches", JSON.stringify(updated));
      } catch (e) {
        console.error("Error saving recent GST searches:", e);
      }
    }

    let targetSlug = target;
    try {
      setIsSearching(true);
      const res = await fetch(`${ENDPOINTS.GST_NUMBER_SEARCH_API}${target}`);
      if (res.ok) {
        const json = await res.json();
        if (json?.success && json?.data) {
          if (json.data.pagePath) {
            targetSlug = json.data.pagePath.replace(/^\/gst-number-search\//, "").replace(/\/+$/, "");
          } else if (json.data.legalName) {
            targetSlug = getGstSlug(json.data.legalName, target);
          }
        }
      }
    } catch (e) {
      console.error("Error fetching GST info for slug:", e);
    } finally {
      setIsSearching(false);
    }

    router.push(`/gst-number-search/${targetSlug}`);
  };

  const handleClearRecent = () => {
    setRecentSearches([]);
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("recentGstSearches");
      } catch (e) { }
    }
  };

  const handleRecentClick = (gstinItem) => {
    setGstinInput(gstinItem);
    handleSearch(gstinItem);
  };

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(gstin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareLink = () => {
    if (typeof window !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };
  const chapterBreadcrumbSchema = getBreadcrumbSchema({
    homeUrl: WEB_URL,
    page: "gst-number-search",
    chapterName: { gstin },
  });

  const pageTitle = isFound
    ? `GST Details for ${legalName} – ${gstin} - ${status} | Credlix`
    : `No record found for GSTIN ${gstin} | Credlix`;
  const pageDescription = isFound
    ? `GST details for ${legalName} (${gstin}): ${constitution}, registered on ${registrationDate}, ${principalPlaceOfBusiness} with ${pincode}. Verify GST number status and business details.`
    : `No registration record found for GSTIN ${gstin} in our GST database. Verify format and state code breakdown.`;

  const schemaData = getServiceSchema({
    metaTitle: pageTitle,
    pageUrl: WEB_URL + router.asPath,
    websiteLogo: IMAGES.SHARED.credlixLogo.src,
    mainImageUrl: IMAGES.HOMEIMG.bigLogoNew.src,
    metaDescription: pageDescription,
    pageName: gstin,
  });

  return (
    <main className="pt-[110px]">
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
        <meta property="og:url" content={WEB_URL + router.asPath} />
        <link rel="canonical" href={WEB_URL + router.asPath} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(chapterBreadcrumbSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>

      <section className="py-12 sm:py-6 bg-[#e0effd]">
        <div className="container mx-auto sm:px-3 px-2">
          <p className="text-sm text-secondary mb-2">
            <Link href={WEB_URL} rel="noopener noreferrer">
              <span className="text-[#2369d8] cursor-pointer font-sm">
                {" "}
                Home {"  "}
              </span>
            </Link>
            {"  "}
            {" > "}
            <Link href={WEB_URL + `/gst-number-search`} rel="noopener noreferrer">
              <span className="text-[#2369d8] cursor-pointer font-sm">
                {" "}
                GST Number Search
              </span>
            </Link>
            {"  "}
            {" > "}
            <span className="font-medium">{gstin}</span>
          </p>

          <div className="flex sm:flex-col items-center justify-between gap-2">
            <div className="sm:w-full w-1/2">
              <h1 className="text-heading1 md:text-heading sm:text-lg font-medium text-baseBlack mb-4 sm:mb-1">
                {isFound
                  ? `GST Details for ${gstin} – ${legalName}`
                  : `GST Details for ${gstin}`}
              </h1>

              <p className="text-baseBlack text-base sm:text-sm mb-4 sm:mb-1">
                {isFound ? (
                  <>
                    <span className="font-bold">{legalName}</span> is registered
                    under GST as a{" "}
                    <span className="font-semibold">{constitution}</span> in{" "}
                    <span className="font-semibold">{state}</span>. Current status:{" "}
                    <span className="font-semibold text-green-700">{status}</span>.
                    Below is the verified registration record sourced from the GST
                    database.
                  </>
                ) : (
                  <>
                    We don't yet hold a registration record for{" "}
                    <span className="font-semibold">{gstin}</span> in our GST database.
                    The format and check digit are valid — see the breakdown below, or search another GSTIN.
                  </>
                )}
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSearch();
                }}
                className="flex items-center gap-2 mb-3"
              >
                <div className="relative flex-1">
                  <span className="absolute left-3 sm:left-2 top-1/2 -translate-y-1/2 text-grayText">
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={gstinInput}
                    onChange={handleInputChange}
                    placeholder="Enter GSTIN"
                    maxLength={15}
                    className="w-full pl-12 pr-4 py-3.5 sm:text-xs bg-white border border-bordercolor rounded-2xl text-baseBlack text-base outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isBtnDisabled}
                  className={`px-5 py-3.5 sm:px-3 text-base sm:text-sm rounded-xl transition-all duration-300 ${isBtnDisabled
                    ? "bg-[#D9D9D9] text-[#6F6F6F] font-medium cursor-not-allowed"
                    : "bg-gradient-to-r from-[#022B5C] to-[#0052CC] hover:from-[#0052CC] hover:to-[#022B5C] text-white font-medium shadow-md"
                    }`}
                >
                  {isSearching ? "Searching..." : "Search"}
                </button>
              </form>

              {recentSearches.length > 0 && (
                <div className="flex items-center gap-xsp mb-3 text-sm flex-wrap">
                  <span className="text-baseBlack text-xs font-medium">
                    Recent
                  </span>
                  {recentSearches.map((item, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleRecentClick(item)}
                      className="px-1 py-xsp bg-white border border-bordercolor rounded-lg text-secondary text-xs"
                    >
                      {item}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={handleClearRecent}
                    className="text-grayText hover:text-baseBlack text-sm"
                  >
                    Clear
                  </button>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-1">
                {gstSearch.heroData.badges.map((badge) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-xsp px-2 py-1 bg-white border border-bordercolor rounded-full text-xs font-medium text-baseBlack"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#10B981]"></span>
                    <span>{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="sm:w-full w-[47%]">
              <div className="bg-white rounded-3xl px-3 py-4 sm:p-2 shadow-normal border border-bordercolor">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold tracking-wider text-grayText uppercase">
                    LIVE GSTIN BREAKDOWN
                  </span>
                  <span className="px-3 py-1 rounded-lg text-xs font-medium bg-green/10 text-green">
                    Valid GSTIN
                  </span>
                </div>

                <div className="space-y-2 sm:space-x-0 mb-2">
                  <div className="flex items-center gap-2 whitespace-nowrap flex-wrap">
                    <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#EEF2FF] text-[#4F46E5]">
                      {stateCode}
                    </div>
                    <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                      State code
                    </div>
                    <div className="text-xs text-baseBlack flex-1">
                      {state}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap flex-wrap">
                    <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#e0effd] text-brand">
                      {pan}
                    </div>
                    <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                      PAN
                    </div>
                    <div className="text-xs text-baseBlack flex-1">
                      PAN of the registered entity
                    </div>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap flex-wrap">
                    <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#DCFCE7] text-[#16A34A]">
                      {entityNo}
                    </div>
                    <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                      Entity no.
                    </div>
                    <div className="text-xs text-baseBlack flex-1">
                      Registration {entityNo} of this PAN in the state
                    </div>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap flex-wrap">
                    <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#FEF9C3] text-[#CA8A04]">
                      {reservedChar}
                    </div>
                    <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                      Reserved
                    </div>
                    <div className="text-xs text-baseBlack flex-1">
                      Always Z
                    </div>
                  </div>
                  <div className="flex items-center gap-2 whitespace-nowrap flex-wrap">
                    <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#F3E8FF] text-[#9333EA]">
                      {checkDigit}
                    </div>
                    <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                      Check digit
                    </div>
                    <div className="text-xs text-baseBlack flex-1">
                      Checksum matches
                    </div>
                  </div>
                </div>

                <div className="p-1 rounded-xl bg-[#FAF5EC] border border-[#F3EAD8] text-baseBlack text-sm">
                  Format and check digit both pass.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isFound ? (
        <section className="pt-6 sm:py-4 bg-[#FAF8F5]">
          <div className="container mx-auto sm:px-3 px-2">
            <div className="bg-white rounded-lg border border-bordercolor overflow-hidden">
              <div className="bg-[#1C1C1C] px-3 py-4 text-white">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
                  <div className="flex items-center gap-3">
                    <span className="text-xl sm:text-base font-bold">
                      {gstin}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]"></span>
                      {status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCopy}
                      type="button"
                      className="bg-[#2C2C2C] hover:bg-[#3C3C3C] text-white text-xs font-medium px-3 py-1.5 rounded-lg border border-[#444] transition-all"
                    >
                      {copied ? "Copied!" : "Copy"}
                    </button>
                    <button
                      type="button"
                      onClick={() => window.print()}
                      className="bg-[#2C2C2C] hover:bg-[#3C3C3C] text-white text-xs font-medium px-3 py-1.5 rounded-lg border border-[#444] transition-all"
                    >
                      PDF
                    </button>
                    <button
                      type="button"
                      onClick={handleShareLink}
                      className="bg-[#2C2C2C] hover:bg-[#3C3C3C] text-white text-xs font-medium px-3 py-1.5 rounded-lg border border-[#444] transition-all"
                    >
                      {linkCopied ? "Link copied" : "Share"}
                    </button>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-base font-bold text-white mb-1 uppercase tracking-wide">
                  {legalName}
                </h2>

                <div className="text-xs text-gray-400 font-normal flex items-center gap-2 flex-wrap">
                  {pan && <span>| PAN {pan}</span>}
                  {constitution && <span>| {constitution}</span>}
                </div>
              </div>

              <div className="px-3 sm:px-2 py-4 bg-[#FAF9F6] border-b border-bordercolor">
                <div className="text-xs font-bold text-baseBlack mb-1">
                  GSTIN BREAKDOWN
                </div>
                <div className="flex flex-wrap -mx-1.5">
                  <div className="w-1/5 md:w-1/3 sm:w-full px-1.5 py-1.5 flex">
                    <div className="w-full bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-col justify-between shadow-xs">
                      <div className="text-lg font-bold text-[#2563EB] mb-1">
                        {stateCode}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-baseBlack">
                          State code
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {state}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-1/5 md:w-1/3 sm:w-full px-1.5 py-1.5 flex">
                    <div className="w-full bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-col justify-between shadow-xs">
                      <div className="text-lg font-bold text-brand mb-1">
                        {pan}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-baseBlack">
                          PAN
                        </div>
                        <div className="text-[11px] text-gray-400">
                          PAN of the registered entity
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-1/5 md:w-1/3 sm:w-full px-1.5 py-1.5 flex">
                    <div className="w-full bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-col justify-between shadow-xs">
                      <div className="text-lg font-bold text-[#16A34A] mb-1">
                        {entityNo}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-baseBlack">
                          Entity no.
                        </div>
                        <div className="text-[11px] text-gray-400">
                          Registration {entityNo} of this PAN in the state
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-1/5 md:w-1/3 sm:w-full px-1.5 py-1.5 flex">
                    <div className="w-full bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-col justify-between shadow-xs">
                      <div className="text-lg font-bold text-[#CA8A04] mb-1">
                        {reservedChar}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-baseBlack">
                          Reserved
                        </div>
                        <div className="text-[11px] text-gray-400">
                          Always Z
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-1/5 md:w-1/3 sm:w-full px-1.5 py-1.5 flex">
                    <div className="w-full bg-white rounded-xl border border-[#E2E8F0] p-3 flex flex-col justify-between shadow-xs">
                      <div className="text-lg font-bold text-[#9333EA] mb-1">
                        {checkDigit}
                      </div>
                      <div>
                        <div className="text-xs font-bold text-baseBlack">
                          Check digit
                        </div>
                        <div className="text-[11px] text-gray-400">
                          Checksum matches
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-3 sm:px-2 py-4 bg-white">
                <div className="flex sm:flex-col gap-4 justify-between">
                  <div className="w-[57%] sm:w-full space-y-4   ">
                    <div className="bg-white rounded-lg border border-bordercolor overflow-hidden shadow-xs">
                      <div className="bg-gray-50 px-4 sm:px-2 py-2 border-b border-bordercolor text-xs font-bold text-gray-400">
                        BUSINESS DETAILS
                      </div>
                      <div className="divide-y divide-bordercolor">
                        <div className="px-4 py-2.5 flex items-start sm:px-2 gap-1">
                          <span className="w-48 sm:w-[40%] text-xs text-gray-400 font-medium shrink-0 pt-0.5">
                            Entity type
                          </span>
                          <span className="text-sm font-medium text-baseBlack flex-1 sm:w-[60%]">
                            {entityType}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-start sm:px-2 gap-1">
                          <span className="w-48 sm:w-[40%] text-xs text-gray-400 font-medium shrink-0 pt-0.5">
                            Constitution of business
                          </span>
                          <span className="text-sm font-medium text-baseBlack flex-1 sm:w-[60%]">
                            {constitution}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-start sm:px-2 gap-1">
                          <span className="w-48 sm:w-[40%] text-xs text-gray-400 font-medium shrink-0 pt-0.5">
                            Registration date
                          </span>
                          <span className="text-sm font-medium text-baseBlack flex-1 sm:w-[60%]">
                            {registrationDate}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-start sm:px-2 gap-1">
                          <span className="w-48 sm:w-[40%] text-xs text-gray-400 font-medium shrink-0 pt-0.5">
                            State jurisdiction
                          </span>
                          <span className="text-xs font-bold text-baseBlack flex-1 sm:w-[60%]">
                            {stateJurisdiction ? stateJurisdiction.replace(/,/g, ", ") : "N/A"}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-start sm:px-2 gap-1">
                          <span className="w-48 sm:w-[40%] text-xs text-gray-400 font-medium shrink-0 pt-0.5">
                            Centre jurisdiction
                          </span>
                          <span className="text-xs font-bold text-baseBlack flex-1 sm:w-[60%]">
                            {centreJurisdiction ? centreJurisdiction.replace(/,/g, ", ") : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg border border-bordercolor overflow-hidden shadow-xs">
                      <div className="bg-gray-50 px-4 sm:px-2 py-2 border-b border-bordercolor text-xs font-bold text-gray-400">
                        PRINCIPAL PLACE OF BUSINESS
                      </div>
                      <div className="py-3 space-y-3">
                        <p className="text-sm px-3 font-medium text-baseBlack leading-relaxed">
                          {principalPlaceOfBusiness || `${state}, India`}
                        </p>
                        <div className="pt-3 px-3 border-t border-bordercolor flex items-center gap-4">
                          <div className="w-1/2">
                            <div className="text-xs text-gray-400 font-medium mb-0.5">
                              State
                            </div>
                            <div className="text-sm font-bold text-baseBlack">
                              {state}
                            </div>
                          </div>
                          <div className="w-1/2">
                            <div className="text-xs text-gray-400 font-medium mb-0.5">
                              Pincode
                            </div>
                            <div className="text-sm font-bold text-baseBlack">
                              {pincode || "N/A"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="w-[40%] sm:w-full space-y-6">
                    <div className="bg-white rounded-lg border border-bordercolor overflow-hidden shadow-xs">
                      <div className="bg-gray-50 px-4 sm:px-2 py-2 border-b border-bordercolor text-xs font-bold text-gray-400">
                        VERIFICATION & COMPLIANCE
                      </div>
                      <div className="divide-y divide-bordercolor">
                        <div className="px-4 py-2.5 flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">
                            Aadhaar verified
                          </span>
                          <span className="px-2 py-xsp rounded-full text-xs font-medium bg-[#E6F4EA] text-[#137333]">
                            {gstData?.aadhaarVerified ? "Yes" : "Yes"}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">
                            Aadhaar verified on
                          </span>
                          <span className="px-2 py-xsp rounded-full text-xs font-medium bg-[#E6F4EA] text-[#137333]">
                            {gstData?.aadhaarVerifiedOn || "25/06/2026"}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">
                            e-KYC verified
                          </span>
                          <span className="px-3 py-xsp rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            {gstData?.ekycVerified ? "Yes" : "—"}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">
                            e-Invoice status
                          </span>
                          <span className="px-2 py-xsp rounded-full text-xs font-bold bg-[#E6F4EA] text-[#137333]">
                            {gstData?.einvoiceStatus ? "Active" : "Active"}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex items-center justify-between">
                          <span className="text-xs text-gray-500 font-medium">
                            Field visit done
                          </span>
                          <span className="px-3 py-xsp rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                            {gstData?.fieldVisitDone ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {hsnSacCodes && hsnSacCodes.length > 0 && (
              <div className="border border-bordercolor rounded-lg bg-white mt-3">
                <div className="bg-gray-50 px-3 py-2 border-b border-bordercolor flex items-center justify-between">
                  <span className="text-xs font-bold sm:font-medium text-gray-400">
                    GOODS & SERVICES — HSN / SAC CODES
                  </span>
                  <span className="text-xs font-normal text-gray-400">
                    {hsnSacCodes.length} declared
                  </span>
                </div>
                <div className="divide-y divide-bordercolor">
                  {hsnSacCodes.map((item, idx) => (
                    <div
                      key={idx}
                      className="px-3 py-2 flex items-center gap-4 sm:gap-2 sm:px-2 text-sm sm:text-xs"
                    >
                      <span className="px-2 py-0.5 rounded text-sm sm:text-xs font-bold bg-red-50 text-brand shrink-0 sm:px-xsp">
                        {item.type || "HSN"}
                      </span>
                      <span className="text-brand font-bold text-sm sm:text-xs shrink-0 w-28 sm:w-16">
                        {item.code}
                      </span>
                      <span className="text-gray-600 font-medium flex-1">
                        {item.description}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </section>
      ) : (
        <section className="pt-16 sm:pt-12 bg-[#FAF8F5]">
          <div className="container mx-auto sm:px-3 px-2">
            <div className="bg-[#FAF8F5] border border-borderColor rounded-2xl px-3 py-5 sm:p-2">
              <div className="flex sm:flex-col items-start justify-between gap-4 pb-2 border-b border-borderColor">
                <div className="flex items-start gap-3 sm:gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-[#e0effd] flex items-center justify-center shrink-0">
                    <svg
                      className="w-5 h-5 text-brand"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <circle cx="11" cy="11" r="6" strokeWidth="2" />
                      <line x1="15.5" y1="15.5" x2="20" y2="20" strokeWidth="2" strokeLinecap="round" />
                      <line x1="9" y1="9" x2="13" y2="13" strokeWidth="2" strokeLinecap="round" />
                      <line x1="13" y1="9" x2="9" y2="13" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xxl sm:text-base font-medium text-black mb-xsp">
                      No record found for this GSTIN
                    </h2>
                    <p className="text-base sm:text-xs text-baseBlack">
                      The format and check digit of{" "}
                      <span className="font-bold text-[#111827]">{gstin}</span> are
                      valid, but we hold no registration against it in our GST
                      database.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 sm:w-full">
                  <button
                    type="button"
                    onClick={() => handleSearch(gstin)}
                    className="px-5 sm:px-2 py-2.5 bg-gradient-to-r from-[#022B5C] to-[#0052CC] hover:from-[#0052CC] hover:to-[#022B5C] text-white rounded-lg text-sm font-semibold transition-all shadow-xs"
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGstinInput("");
                      if (typeof document !== "undefined") {
                        const inputEl = document.querySelector('input[type="text"]');
                        if (inputEl) inputEl.focus();
                      }
                    }}
                    className="px-5 sm:px-2 py-2.5 bg-white border border-[#D1D5DB] hover:bg-gray-50 text-[#374151] rounded-lg text-sm font-medium transition-all shadow-xs"
                  >
                    Search another
                  </button>
                </div>
              </div>

              <div className="flex sm:flex-col items-start gap-4 pt-6">
                <div className="w-1/2 sm:w-full">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
                    WHY THIS HAPPENS
                  </h3>
                  <ul className="space-y-4 text-sm sm:text-xs text-[#4B5563] leading-relaxed">
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0"></span>
                      <span>
                        A digit or letter is wrong. A GSTIN can pass the checksum
                        and still never have been issued — compare it against the
                        invoice or bid document character by character.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0"></span>
                      <span>
                        The registration is newer than our records. Our GST data is a
                        snapshot of the government register, so a recent
                        registration may not appear yet.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand mt-2 shrink-0"></span>
                      <span>
                        The entry may have changed since our last update. For a
                        number you are about to rely on in a bid, confirm it on the
                        government GST portal before treating it as invalid.
                      </span>
                    </li>
                  </ul>
                  <div className="flex items-center gap-4 sm:gap-1 mt-3 flex-wrap text-sm">
                    {isRequested ? (
                      <span className="text-brand font-bold">
                        Thanks — we'll add it
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsRequested(true)}
                        className="text-brand font-bold hover:underline cursor-pointer"
                      >
                        Ask us to add this GSTIN
                      </button>
                    )}
                    <Link
                      href="https://services.gst.gov.in/services/searchtp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand font-medium hover:underline"
                    >
                      Check on the government portal
                    </Link>
                  </div>
                </div>

                <div className="w-1/2 sm:w-full md:w-full">
                  <div className="bg-white rounded-3xl px-3 py-4 sm:p-2 shadow-normal border border-bordercolor">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold tracking-wider text-grayText uppercase">
                        What the number itself says
                      </span>
                    </div>

                    <div className="space-y-2 sm:space-x-0 mb-2">
                      <div className="flex items-center gap-1 whitespace-nowrap flex-wrap">
                        <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#EEF2FF] text-[#4F46E5]">
                          {stateCode}
                        </div>
                        <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                          State code
                        </div>
                        <div className="text-xs text-baseBlack flex-1">
                          {state}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 whitespace-nowrap flex-wrap">
                        <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#e0effd] text-brand">
                          {pan}
                        </div>
                        <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                          PAN
                        </div>
                        <div className="text-xs text-baseBlack flex-1">
                          PAN of the registered entity
                        </div>
                      </div>
                      <div className="flex items-center gap-1 whitespace-nowrap flex-wrap">
                        <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#DCFCE7] text-[#16A34A]">
                          {entityNo}
                        </div>
                        <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                          Entity no.
                        </div>
                        <div className="text-xs text-baseBlack flex-1">
                          Registration {entityNo} of this PAN in the state
                        </div>
                      </div>
                      <div className="flex items-center gap-1 whitespace-nowrap flex-wrap">
                        <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#FEF9C3] text-[#CA8A04]">
                          {reservedChar}
                        </div>
                        <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                          Reserved
                        </div>
                        <div className="text-xs text-baseBlack flex-1">
                          Always Z
                        </div>
                      </div>
                      <div className="flex items-center gap-1 whitespace-nowrap flex-wrap">
                        <div className="w-32 sm:w-28 h-10 rounded-xl flex items-center justify-center font-medium text-sm bg-[#F3E8FF] text-[#9333EA]">
                          {checkDigit}
                        </div>
                        <div className="w-24 text-sm font-medium text-baseBlack shrink-0">
                          Check digit
                        </div>
                        <div className="text-xs text-baseBlack flex-1">
                          Checksum matches
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      <HsnSearchCTA />
    </main>
  );
};

export default GSTNumberDetail;

export async function getServerSideProps(context) {
  const { params } = context;
  const rawSlug = params?.gstNumberSearch || "";
  const gstin = extractGstinFromSlug(rawSlug);
  let gstData = null;

  if (gstin && gstin.length === 15) {
    try {
      const apiRes = await fetch(`${ENDPOINTS.GST_NUMBER_SEARCH_API}${gstin}`);
      if (apiRes.ok) {
        const json = await apiRes.json();
        if (json.success && json.data && json.data.found) {
          gstData = json.data;
        }
      }
    } catch (error) {
      console.error("Error fetching GST API:", error);
    }
  }

  // Canonical slug redirect if found
  if (gstData) {
    let canonicalSlug = "";
    if (gstData.pagePath) {
      canonicalSlug = gstData.pagePath.replace(/^\/gst-number-search\//, "").replace(/\/+$/, "");
    } else if (gstData.legalName) {
      canonicalSlug = getGstSlug(gstData.legalName, gstin);
    }

    if (canonicalSlug && rawSlug !== canonicalSlug) {
      return {
        redirect: {
          destination: `/gst-number-search/${canonicalSlug}`,
          permanent: true,
        },
      };
    }
  }

  return {
    props: {
      gstData,
      slug: rawSlug,
    },
  };
}